'use client';

import { formatDistanceToNow } from 'date-fns';
import { KeyRound, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@/components/ui';
import {
  useRemoveCredential,
  useSetCredential,
  useSyncInventory,
  useTestCredential,
} from '@/hooks/usePartners';
import type { PartnerDetail } from '@/lib/types';

/**
 * The partner's Flespi token.
 *
 * There is no endpoint that returns one, so this shows the hint and the result
 * of the last check — which is all an operator needs about a token they cannot
 * read. Storing one tests it first; a token that does not work is not worth
 * keeping, and finding out at allocation time is finding out too late.
 */
export function CredentialCard({ partner }: { partner: PartnerDetail }) {
  const [token, setToken] = useState('');

  const setCredential = useSetCredential(partner.id);
  const removeCredential = useRemoveCredential(partner.id);
  const testCredential = useTestCredential(partner.id);
  const sync = useSyncInventory(partner.id);

  // A partner that only inherits the token has no pool of its own to sync.
  const ownsItsPool = partner.tokenHolder?.id === partner.id;

  const store = async () => {
    if (token.trim().length < 16) {
      toast.error('That does not look like a Flespi token');
      return;
    }
    try {
      const stored = await setCredential.mutateAsync(token.trim());
      setToken('');
      toast.success(`Token ${stored.hint} stored`, {
        description: 'Sync the pool to see its devices.',
      });
    } catch (error) {
      toast.error('Could not store the token', {
        description: error instanceof Error ? error.message : 'Something went wrong',
      });
    }
  };

  const test = async () => {
    try {
      const result = await testCredential.mutateAsync();
      if (result.ok) {
        toast.success(result.isInherited ? 'The inherited token works' : 'The token works');
      } else {
        toast.error('Flespi refused the token', { description: result.error ?? undefined });
      }
    } catch (error) {
      toast.error('Could not test the token', {
        description: error instanceof Error ? error.message : 'Something went wrong',
      });
    }
  };

  const pull = async () => {
    try {
      const result = await sync.mutateAsync();
      toast.success(`${result.devices} device(s) across ${result.channels} channel(s)`, {
        description: result.isInherited
          ? 'Read with the integrator’s token.'
          : result.removed > 0
            ? `${result.removed} free device(s) no longer in Flespi were dropped.`
            : undefined,
      });
    } catch (error) {
      toast.error('Could not sync', {
        description: error instanceof Error ? error.message : 'Something went wrong',
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-4 w-4" />
          Flespi token
        </CardTitle>
        <CardDescription>
          {partner.parentId
            ? 'A sub-dealer inherits its integrator’s token. Store one here only to override it.'
            : 'This token is what makes the device pool visible. It is encrypted at rest and never served back.'}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {partner.credential ? (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/40 p-3">
            <code className="text-sm">{partner.credential.hint}</code>
            <Badge variant={partner.credential.lastCheckOk === false ? 'destructive' : 'success'}>
              {partner.credential.lastCheckOk === false ? 'Failing' : 'Stored'}
            </Badge>
            {partner.credential.lastCheckedAt && (
              <span className="text-sm text-muted-foreground">
                checked{' '}
                {formatDistanceToNow(new Date(partner.credential.lastCheckedAt), {
                  addSuffix: true,
                })}
              </span>
            )}
            {partner.credential.lastCheckError && (
              <p className="w-full text-sm text-error">{partner.credential.lastCheckError}</p>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {partner.parentId
              ? 'No token of its own; it uses the one above it in the chain.'
              : 'No token stored yet, so this partner has no pool.'}
          </p>
        )}

        <div className="space-y-2">
          <Label htmlFor="flespi-token">
            {partner.credential ? 'Replace the token' : 'Store a token'}
          </Label>
          <div className="flex gap-2">
            <Input
              id="flespi-token"
              type="password"
              autoComplete="off"
              value={token}
              onChange={(event) => setToken(event.target.value)}
              placeholder="FlespiToken …"
            />
            <Button onClick={store} disabled={setCredential.isPending || token.trim() === ''}>
              {setCredential.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Store
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={test} disabled={testCredential.isPending}>
            {testCredential.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Test
          </Button>
          <Button
            variant="outline"
            onClick={pull}
            disabled={sync.isPending || !ownsItsPool}
            title={
              ownsItsPool
                ? undefined
                : `The pool belongs to ${partner.tokenHolder?.name ?? 'the partner above'}; sync it there`
            }
          >
            {sync.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Sync channels and devices
          </Button>
          {partner.credential && (
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  await removeCredential.mutateAsync();
                  toast.success('Token removed');
                } catch (error) {
                  toast.error('Could not remove the token', {
                    description: error instanceof Error ? error.message : undefined,
                  });
                }
              }}
              disabled={removeCredential.isPending}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Remove
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
